import { useEffect, useRef } from "react";

/**
 * Auto-fit text inside its container by width AND optionally height.
 * Returns a ref to attach to the text element.
 *
 * Options:
 *   - max (px): starting font size
 *   - min (px): hard floor
 *   - padding (px): subtracted from container width
 *   - containerRef: ref to a wrapper element (defaults to parentElement)
 *   - maxHeightRatio (0..1): if set, also clamps font so element height stays under
 *                            `containerRef.clientHeight * maxHeightRatio`. Defaults to 1 (no height clamp).
 *
 * Strategy:
 *   - Switch to `display: inline-block` while measuring (so scrollWidth/Height reflect content)
 *   - Shrink in 5% steps until both bounds satisfied (or `min` reached)
 *   - Re-runs on text change, container resize (ResizeObserver), and window resize
 *   - Uses requestAnimationFrame to avoid ResizeObserver-loop warnings
 */
export const useFitText = (text, options = {}) => {
  const {
    max = 280,
    min = 60,
    padding = 12,
    containerRef = null,
    maxHeightRatio = 1,
    widthRatio = 1,
  } = options;
  const textRef = useRef(null);

  useEffect(() => {
    const fit = () => {
      const el = textRef.current;
      const container = containerRef?.current || el?.parentElement;
      if (!el || !container) return false;

      // Read container's actual padding so we never overflow past it.
      const cs = window.getComputedStyle(container);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);

      const maxW = (container.clientWidth - padX) * widthRatio - padding;
      const maxH =
        maxHeightRatio < 1 && container.clientHeight > 0
          ? (container.clientHeight - padY) * maxHeightRatio - padding
          : maxHeightRatio >= 1 && container.clientHeight > 0
          ? container.clientHeight - padY - padding
          : Infinity;
      // Container not laid out yet — caller will retry on the next animation frame.
      if (maxW <= 0) return false;

      const prevDisplay = el.style.display;
      el.style.display = "inline-block";

      let size = max;
      el.style.fontSize = `${size}px`;
      let safety = 60;
      while (
        safety-- > 0 &&
        size > min &&
        (el.scrollWidth > maxW || el.scrollHeight > maxH)
      ) {
        const step = Math.max(2, Math.round(size * 0.05));
        size -= step;
        el.style.fontSize = `${size}px`;
      }

      el.style.display = prevDisplay;
      return true;
    };

    // Initial fit may run before layout has settled (container.clientWidth === 0).
    // Retry on the next animation frames until it succeeds or we hit the cap.
    let retryRafId = null;
    let attempts = 0;
    const tryFit = () => {
      retryRafId = null;
      const ok = fit();
      if (!ok && attempts++ < 30) {
        retryRafId = window.requestAnimationFrame(tryFit);
      }
    };
    tryFit();
    let rafId = null;
    const scheduleFit = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        fit();
      });
    };
    const ro = new ResizeObserver(scheduleFit);
    const target = containerRef?.current || textRef.current?.parentElement;
    if (target) ro.observe(target);
    window.addEventListener("resize", scheduleFit);
    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (retryRafId !== null) window.cancelAnimationFrame(retryRafId);
      ro.disconnect();
      window.removeEventListener("resize", scheduleFit);
    };
  }, [text, max, min, padding, containerRef, maxHeightRatio, widthRatio]);

  return textRef;
};
